/**
 * Agent consent policy — query + mutation hooks.
 *
 * GET is a plain read (agents.read); PUT and DELETE are step-up-gated server-side
 * (agents.manage, 5-minute freshness), so both mutations go through withStepUp —
 * configuring maker-checker and the re-consent deadline is a governance act.
 *
 * Mirrors the sibling agent-approval-thresholds hooks (endpoint/query-key style, step-up
 * wrapper, cache update on success).
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiFetch, type ApiError } from "@/lib/api-client";
import { withStepUp } from "@/lib/with-step-up";
import {
  agentConsentPolicySchema,
  type AgentConsentPolicy,
  type AgentConsentPolicyUpdate,
} from "./schemas";

function endpoint(tenantID: string): string {
  return `/api/v1/tenants/${encodeURIComponent(tenantID)}/agent-consent-policy`;
}

const queryKey = (tenantID: string) => ["agents", "consent-policy", tenantID] as const;

export async function fetchAgentConsentPolicy(tenantID: string): Promise<AgentConsentPolicy> {
  const raw = await apiFetch<unknown>(endpoint(tenantID));
  return agentConsentPolicySchema.parse(raw);
}

export function useConsentPolicyQuery(tenantID: string) {
  return useQuery<AgentConsentPolicy, ApiError>({
    queryKey: queryKey(tenantID),
    queryFn: () => fetchAgentConsentPolicy(tenantID),
    enabled: tenantID.length > 0,
    staleTime: 60_000,
  });
}

// Saves the tenant consent policy (maker-checker + re-consent deadline). Step-up-gated.
export function useUpdateConsentPolicyMutation(tenantID: string) {
  const qc = useQueryClient();
  return useMutation<AgentConsentPolicy, ApiError, AgentConsentPolicyUpdate>({
    mutationFn: (body) =>
      withStepUp(() =>
        apiFetch<unknown>(endpoint(tenantID), { method: "PUT", body }),
      ).then((raw) => agentConsentPolicySchema.parse(raw)),
    onSuccess: (policy) => {
      qc.setQueryData(queryKey(tenantID), policy);
    },
  });
}

// Clears the tenant consent policy row — reverts to platform defaults. Step-up-gated.
export function useClearConsentPolicyMutation(tenantID: string) {
  const qc = useQueryClient();
  return useMutation<AgentConsentPolicy, ApiError, void>({
    mutationFn: () =>
      withStepUp(() => apiFetch<unknown>(endpoint(tenantID), { method: "DELETE" })).then((raw) =>
        agentConsentPolicySchema.parse(raw),
      ),
    onSuccess: (policy) => {
      qc.setQueryData(queryKey(tenantID), policy);
    },
  });
}
